import { MouseEvent } from 'react';
import { Button, ButtonProps } from '@shared/components/Button';
import { useMenuContext } from './MenuContext';

/**
 * Menu.Button
 *
 * A pre-styled trigger button that toggles the menu. Shows an optional
 * down-arrow indicator and an active state while the menu is open. Use this
 * when you want a standard button trigger; use `Menu.Trigger` when you need to
 * wrap a custom element instead.
 */

export function MenuButton({
  variant = 'select',
  iconOnly,
  showDownArrow = !iconOnly,
  style,
  onClick: onClickProp,
  ...props
}: Partial<ButtonProps>) {
  const { toggle, isOpen, anchorName } = useMenuContext();

  const onClick = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    await onClickProp?.(event);
    if (event.defaultPrevented) {
      return;
    }
    toggle();
  };

  const mergedProps = {
    variant,
    iconOnly,
    showDownArrow,
    ...props,
  } as ButtonProps;

  return (
    <Button
      {...mergedProps}
      style={{ anchorName, ...style }}
      onClick={onClick}
      aria-haspopup="menu"
      aria-expanded={isOpen || undefined}
    />
  );
}
